/**
 * `RelativeTime` — humanized "Xh ago" timestamp with the full
 * ISO-derived date+time available on hover.
 *
 * Replaces the dashboard-wide pattern of
 *   `<span title={formatIsoDate(iso)}>{formatRelativeTime(iso)}</span>`
 * (and the many call sites that rendered the relative form WITHOUT the
 * hover affordance), so every relative timestamp now carries the same
 * fallback to a precise UTC instant on hover.
 *
 * As of `fix/dry-run-tooltip-mobile-hover` the hover is rendered through
 * the styled `Tooltip` atom instead of the raw `title` attribute — the
 * native browser tooltip is unstyled, slow to appear, and reads with a
 * jarring system font on top of the dashboard's tight visual language.
 * The Tooltip atom is hover + focus accessible and renders in the same
 * mono/serif chrome as the rest of the surface. Existing call sites are
 * unaffected: `style`, `className`, `display` passthrough still target
 * the visible relative-text element (the tooltip wrapper itself is a
 * zero-cost inline `<span>` so layout-affecting styles like
 * `marginLeft: 'auto'` flow through to its outer wrapper via the
 * Tooltip atom's `style` / `className` props).
 *
 * Reads the effective display time zone (ADR-0160) so the hover tooltip's
 * precise instant renders in the merchant's chosen zone; the relative text
 * ("Xh ago") is zone-independent. Outside a `TimeZoneProvider` the hook
 * returns 'UTC', so the tooltip is byte-identical to the pre-ADR-0160 form.
 *
 * Kept app-local (not promoted to `@sill/ui`) until `apps/marketing`
 * lands and the duplication question is concrete — same rationale as
 * the underlying `format.ts` helpers (ADR-0003).
 */

import type { CSSProperties } from 'react';

import { useDisplayTimeZone } from '../../app/time-zone-context.js';
import { formatIsoDate, formatRelativeTime } from '../../lib/format.js';
import { Tooltip } from './Tooltip.js';

export interface RelativeTimeProps {
  /** ISO-8601 instant (e.g. an audit record's `evaluated_at`). */
  iso: string;
  /** Inline style passthrough so the migrated call site keeps its
   *  existing font/color/letter-spacing. */
  style?: CSSProperties;
  /** Class passthrough (e.g. tailwind layout utilities the call site
   *  applied to its previous `<span>`). */
  className?: string;
  /** Test id passthrough so suites that anchored on a wrapper span
   *  can still target this atom. */
  'data-testid'?: string;
  /**
   * Override the rendered relative text — e.g. when the call site
   *  formatted the relative form differently ("12s" without the "ago"
   *  suffix for table cells where the suffix is in the template). The
   *  hover tooltip still derives from `iso`, so the precise UTC
   *  instant remains available even with a custom display string.
   */
  display?: string;
}

export function RelativeTime({
  iso,
  style,
  className,
  'data-testid': testid,
  display,
}: RelativeTimeProps): JSX.Element {
  const timeZone = useDisplayTimeZone();
  // The Tooltip wrapper is the OUTER element — `style` / `className` /
  // `data-testid` ride on it so layout-affecting properties (e.g.
  // `marginLeft: 'auto'` inside a flex row, `display: 'block'` inside
  // the Mandate-detail Row cell) target the element that participates
  // in the parent's layout. The inner span holds only the visible
  // relative text. The Tooltip atom uses `display: inline` so the
  // baseline matches the surrounding mono / sans text in a row — the
  // default `inline-flex` would otherwise lift the cell's baseline.
  return (
    <Tooltip
      content={formatIsoDate(iso, timeZone)}
      displayMode="inline"
      {...(style !== undefined ? { style } : {})}
      {...(className !== undefined ? { className } : {})}
      {...(testid !== undefined ? { 'data-testid': testid } : {})}
    >
      <span>{display ?? formatRelativeTime(iso)}</span>
    </Tooltip>
  );
}
